"use client";

import {
  useState,
  useEffect,
  useRef,
  useCallback,
  type ReactPortal,
} from "react";
import { createPortal } from "react-dom";
import { Wrapper } from "@googlemaps/react-wrapper";
import type { ContactInfo, SocialMediaLink } from "@/lib/navigation";
import type { TravelAgency } from "@/lib/findAgent";
import AgentMapView from "./AgentMapView";
import AgentCard from "./AgentCard";
import styles from "./find-agent.module.css";

const API_KEY = process.env.NEXT_PUBLIC_GOOGLE_API_KEY || "";
const API_URL = process.env.NEXT_PUBLIC_API_URL || "";
const COMPANY_ID = process.env.NEXT_PUBLIC_COMPANY_ID || "";
const ACCESS_TOKEN = process.env.NEXT_PUBLIC_ACCESS_TOKEN || "";
const PER_PAGE = 15;

// ─── Pagination Helper ──────────────────────────────────────────────

function buildPageNumbers(
  current: number,
  total: number
): (number | "...")[] {
  if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | "...")[] = [1];
  const left = Math.max(2, current - 1);
  const right = Math.min(total - 1, current + 1);
  if (left > 2) pages.push("...");
  for (let i = left; i <= right; i++) pages.push(i);
  if (right < total - 1) pages.push("...");
  pages.push(total);
  return pages;
}

interface Props {
  contactInfo: ContactInfo;
  socialMedia: SocialMediaLink[];
  title: string;
  description: string;
  coverPhoto: string;
}

// Props kept for API compatibility with FindAgentSection

// ─── Places Autocomplete ────────────────────────────────────────────

function PlacesInput({
  onSelect,
}: {
  onSelect: (lat: number, lng: number) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [predictions, setPredictions] = useState<
    google.maps.places.AutocompletePrediction[]
  >([]);
  const [value, setValue] = useState("");
  const serviceRef = useRef<google.maps.places.AutocompleteService | null>(
    null
  );
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(
    null
  );

  useEffect(() => {
    serviceRef.current = new google.maps.places.AutocompleteService();
    const div = document.createElement("div");
    placesServiceRef.current = new google.maps.places.PlacesService(div);
  }, []);

  const handleInput = (text: string) => {
    setValue(text);
    if (!text.trim() || !serviceRef.current) {
      setPredictions([]);
      return;
    }
    serviceRef.current.getPlacePredictions(
      { input: text, types: ["geocode"] },
      (results) => setPredictions(results || [])
    );
  };

  const handleSelect = (placeId: string, description: string) => {
    setValue(description);
    setPredictions([]);
    if (!placesServiceRef.current) return;
    placesServiceRef.current.getDetails(
      { placeId, fields: ["geometry"] },
      (place) => {
        const lat = place?.geometry?.location?.lat();
        const lng = place?.geometry?.location?.lng();
        if (lat && lng) onSelect(lat, lng);
      }
    );
  };

  return (
    <div className={styles.placesWrapper}>
      <i className={`fa-light fa-location-dot ${styles.placesIcon}`} />
      <input
        ref={inputRef}
        type="text"
        placeholder="Enter address, city, or zip code..."
        value={value}
        onChange={(e) => handleInput(e.target.value)}
        onBlur={() => setTimeout(() => setPredictions([]), 200)}
        autoComplete="off"
      />
      {predictions.length > 0 && (
        <ul className={styles.suggestions}>
          {predictions.map((p) => (
            <li
              key={p.place_id}
              className={styles.suggestionItem}
              onMouseDown={() => handleSelect(p.place_id, p.description)}
            >
              <i className="fa-light fa-location-dot" />
              {p.description}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────

function FindAgentInner() {
  const resultsScrollRef = useRef<HTMLElement | null>(null);
  const resultsTopRef = useRef<HTMLElement | null>(null);
  const pendingScrollRef = useRef(false);
  const [coords, setCoords] = useState({ lat: 40.99783, lng: -74.34761 });
  const [range, setRange] = useState(10);
  const [allAgencies, setAllAgencies] = useState<TravelAgency[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPlace, setSelectedPlace] = useState<{
    lat: number;
    lng: number;
  } | null>(null);

  // Portal targets from server-rendered shell
  const [targets, setTargets] = useState<{
    map: HTMLElement | null;
    search: HTMLElement | null;
    results: HTMLElement | null;
    resultsTop: HTMLElement | null;
    pagination: HTMLElement | null;
  }>({ map: null, search: null, results: null, resultsTop: null, pagination: null });

  const totalPages = Math.ceil(allAgencies.length / PER_PAGE);
  const paginatedAgencies = allAgencies.slice(
    (currentPage - 1) * PER_PAGE,
    currentPage * PER_PAGE
  );

  // Find portal targets + measure header height
  useEffect(() => {
    const map = document.getElementById("find-agent-map");
    const search = document.getElementById("find-agent-search");
    const results = document.getElementById("find-agent-results");
    const resultsTop = document.getElementById("find-agent-results-top");
    const pagination = document.getElementById("find-agent-pagination");
    // Clear server-rendered content before portals mount
    [map, search, results, resultsTop, pagination].forEach((el) => {
      if (el) el.innerHTML = "";
    });

    setTargets({ map, search, results, resultsTop, pagination });
    resultsScrollRef.current = results;
    resultsTopRef.current = resultsTop;

    // Set --header-h for layout height calc
    const layout = map?.closest(`.${styles.layout}`) as HTMLElement | null;
    if (layout) {
      const update = () => {
        const top = layout.getBoundingClientRect().top + window.scrollY;
        layout.style.setProperty("--header-h", `${top}px`);
      };
      update();
      window.addEventListener("resize", update);
      return () => window.removeEventListener("resize", update);
    }
  }, []);

  // Add footer padding on mobile
  useEffect(() => {
    const footer = document.querySelector<HTMLElement>(".footer-wrapper");
    const isMobile = window.innerWidth < 992;
    if (isMobile && footer) footer.style.paddingBottom = "56px";
    return () => {
      if (footer) footer.style.paddingBottom = "";
    };
  }, []);

  const scrollToResults = useCallback(() => {
    requestAnimationFrame(() => {
      if (window.innerWidth < 992) {
        resultsTopRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      } else {
        resultsScrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
      }
    });
  }, []);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    scrollToResults();
  };

  const fetchAgencies = useCallback(
    async (lat: number, lng: number, r: number, scroll = false) => {
      setLoading(true);
      if (scroll) pendingScrollRef.current = true;
      try {
        const res = await fetch(
          `${API_URL}crm/travelagency/in_range?Range=${r}&CompanyId=${COMPANY_ID}&UserFrontendGeoInfo=true&lat=${lat}&lang=${lng}`,
          {
            headers: {
              Authorization: `Bearer ${ACCESS_TOKEN}`,
              "Content-Type": "application/json",
            },
          }
        );
        const data = await res.json();
        setAllAgencies(data?.data?.travelAgencies ?? []);
        setCurrentPage(1);
      } catch {
        setAllAgencies([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Scroll to results after loading completes
  useEffect(() => {
    if (!loading && pendingScrollRef.current) {
      pendingScrollRef.current = false;
      scrollToResults();
    }
  }, [loading, scrollToResults]);

  useEffect(() => {
    if (!navigator.geolocation) {
      fetchAgencies(coords.lat, coords.lng, range);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setCoords({ lat: latitude, lng: longitude });
        fetchAgencies(latitude, longitude, range);
      },
      () => {
        fetchAgencies(coords.lat, coords.lng, range);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    if (selectedPlace) {
      setCoords({ lat: selectedPlace.lat, lng: selectedPlace.lng });
      fetchAgencies(selectedPlace.lat, selectedPlace.lng, range, true);
    } else {
      fetchAgencies(coords.lat, coords.lng, range, true);
    }
  };

  // Build portals into server-rendered shell
  const portals: ReactPortal[] = [];

  // Map portal
  if (targets.map) {
    portals.push(
      createPortal(
        <div className={styles.mapFill}>
          <AgentMapView
            center={coords}
            agencies={paginatedAgencies}
            currentPage={currentPage}
          />
        </div>,
        targets.map,
        "map"
      )
    );
  }

  // Search bar portal
  if (targets.search) {
    portals.push(
      createPortal(
        <div className={styles.searchCard}>
          <div className={styles.searchBar}>
            <select
              className={styles.rangeSelect}
              value={range}
              onChange={(e) => setRange(Number(e.target.value))}
            >
              <option value={10}>10 mi</option>
              <option value={20}>20 mi</option>
              <option value={25}>25 mi</option>
              <option value={30}>30 mi</option>
              <option value={50}>50 mi</option>
            </select>
            <div className={styles.searchDivider} />
            <PlacesInput
              onSelect={(lat, lng) => {
                setSelectedPlace({ lat, lng });
                setCoords({ lat, lng });
                fetchAgencies(lat, lng, range, true);
              }}
            />
            <button className={styles.searchBtn} onClick={handleSearch}>
              <i className="fa-solid fa-search" />
              <span className={styles.searchBtnText}>Search</span>
            </button>
          </div>
        </div>,
        targets.search,
        "search"
      )
    );
  }

  // Results header count portal
  if (targets.resultsTop) {
    portals.push(
      createPortal(
        <>
          <h3>Nearby Agents</h3>
          {!loading && allAgencies.length > 0 && (
            <span className={styles.totalText}>
              {allAgencies.length} found
            </span>
          )}
        </>,
        targets.resultsTop,
        "results-top"
      )
    );
  }

  // Results portal
  if (targets.results) {
    portals.push(
      createPortal(
        loading ? (
          <div className={styles.skeleton}>
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className={styles.skeletonCard}>
                <div className={styles.skeletonCardInner}>
                  <div className={styles.skeletonTopRow}>
                    <div
                      className={`${styles.skeletonPulse} ${styles.skeletonIndex}`}
                    />
                    <div
                      className={`${styles.skeletonPulse} ${styles.skeletonAvatar}`}
                    />
                  </div>
                  <div className={styles.skeletonLines}>
                    <div
                      className={`${styles.skeletonPulse} ${styles.skeletonLine}`}
                    />
                    <div
                      className={`${styles.skeletonPulse} ${styles.skeletonLine}`}
                    />
                    <div
                      className={`${styles.skeletonPulse} ${styles.skeletonLine}`}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : paginatedAgencies.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>
              <i className="fa-light fa-map-location-dot" />
            </div>
            <h4>No Agents Found</h4>
            <p>
              Try expanding your search radius or searching a different address.
            </p>
          </div>
        ) : (
          <div className={styles.agentList}>
            {paginatedAgencies.map((agency, i) => (
              <AgentCard
                key={agency.id}
                agency={agency}
                index={i + 1 + PER_PAGE * (currentPage - 1)}
              />
            ))}
          </div>
        ),
        targets.results,
        "results"
      )
    );
  }

  // Pagination portal
  if (targets.pagination && !loading && totalPages > 1) {
    portals.push(
      createPortal(
        <div className={styles.paginationWrap}>
          <div className={styles.pagination}>
            <button
              className={styles.pageNav}
              disabled={currentPage === 1}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              <i className="fa-solid fa-chevron-left" />
            </button>
            {buildPageNumbers(currentPage, totalPages).map((item, i) =>
              item === "..." ? (
                <span key={`e${i}`} className={styles.ellipsis}>
                  &hellip;
                </span>
              ) : (
                <button
                  key={item}
                  className={`${styles.pageBtn} ${item === currentPage ? styles.pageBtnActive : ""}`}
                  onClick={() => handlePageChange(item as number)}
                >
                  {item}
                </button>
              )
            )}
            <button
              className={styles.pageNav}
              disabled={currentPage === totalPages}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              <i className="fa-solid fa-chevron-right" />
            </button>
          </div>
          <span className={styles.pageInfo}>
            {currentPage} / {totalPages}
          </span>
        </div>,
        targets.pagination,
        "pagination"
      )
    );
  }

  return <>{portals}</>;
}

// ─── Wrapper ────────────────────────────────────────────────────────

export default function FindAgentClient(props: Props) {
  return (
    <Wrapper apiKey={API_KEY} libraries={["places", "marker"]}>
      <FindAgentInner />
    </Wrapper>
  );
}
